/**
 * No dialog of the app can be moved by its header (MYAC-1482).
 *
 * PrimeVue makes `Dialog` and `ConfirmDialog` draggable by default, and a
 * draggable header swallows the mouse: pressing on the title starts a drag
 * (and sets `user-select: none` on the body), so a user cannot select and copy
 * a requirement's, a document's or a task's title. Moving a dialog serves
 * nobody here, so the default is flipped once for the whole app instead of
 * `:draggable="false"` on each of the ~100 usages — a new dialog is right
 * without its author knowing.
 *
 * PrimeVue 4.5 has no global prop defaults (its config only carries theme, pt,
 * locale…), so this rewrites the `draggable` default on the component
 * definition itself. Vue copies a prop's options the first time the component
 * is used, so this must run before anything mounts. An explicit
 * `:draggable="true"` still works.
 *
 * The prop lives on the `Base*` component each one extends, not on the
 * component; the lookup walks `extends` and throws if PrimeVue ever moves it,
 * so an upgrade fails loudly in `primevueDialogDefaults.test.ts` rather than
 * silently bringing movable dialogs back.
 */
import type { Component } from 'vue'
import ConfirmDialog from 'primevue/confirmdialog'
import Dialog from 'primevue/dialog'

type PropOptions = { default?: unknown }
type ComponentDefinition = { name?: string; props?: Record<string, PropOptions>; extends?: ComponentDefinition }

function findPropOptions(component: Component, prop: string): PropOptions {
  for (let def = component as ComponentDefinition | undefined; def; def = def.extends) {
    const options = def.props?.[prop]
    if (options) return options
  }
  throw new Error(`PrimeVue ${(component as ComponentDefinition).name} has no "${prop}" prop any more`)
}

export const STATIC_DIALOG_COMPONENTS: Component[] = [Dialog, ConfirmDialog]

export function makePrimeVueDialogsStatic(): void {
  for (const component of STATIC_DIALOG_COMPONENTS) {
    findPropOptions(component, 'draggable').default = false
  }
}
